import { Toggle } from '@base-ui/react/toggle';
import { ToggleGroup } from '@base-ui/react/toggle-group';
import { Toolbar } from '@base-ui/react/toolbar';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { DaemonStatusPopover } from '@/components/status-bar/DaemonStatusPopover';
import { LastScanPopover } from '@/components/status-bar/LastScanPopover';
import { HoverTip } from '@/components/ui/HoverTip';
import { KeyboardShortcutKey } from '@/components/ui/KeyboardShortcutKey';
import { cx } from '@/cva.config';
import { useRunScanMutation } from '@/hooks/useRunScanMutation';
import { daemonStatusQueryOptions, lastScanQueryOptions } from '@/lib/api';
import { formatRelative } from '@/lib/format-duration';
import {
  type Density,
  useDisplayDensity,
} from '@/providers/dashboard/ShellContext';

const densityOptions = [
  { id: 'comfortable', label: '≣', tooltip: 'Comfortable density' },
  { id: 'compact', label: '≡', tooltip: 'Compact density' },
] as const satisfies ReadonlyArray<{
  id: Density;
  label: string;
  tooltip: string;
}>;

const computeNextLabel = (
  nextScanAt: string | null | undefined,
  running: boolean | undefined,
) => {
  if (running) {
    return 'now';
  }

  if (!nextScanAt) {
    return '—';
  }

  const diff = new Date(nextScanAt).getTime() - Date.now();

  if (diff <= 0) {
    return '—';
  }

  const mins = Math.ceil(diff / 60_000);

  return `${mins}m`;
};

export const StatusBar = () => {
  const { density, setDensity } = useDisplayDensity();
  const { data: daemonData, isError: daemonError } = useQuery(
    daemonStatusQueryOptions(),
  );
  const { data: lastScanData, isError: lastScanError } = useQuery(
    lastScanQueryOptions(),
  );
  const runScan = useRunScanMutation();

  const [daemonOpen, setDaemonOpen] = useState(false);
  const [lastScanOpen, setLastScanOpen] = useState(false);

  const daemonLabelText = () => {
    if (!daemonData) {
      return 'daemon · …';
    }

    if (daemonData.stale) {
      return 'daemon · stale';
    }

    if (daemonData.running) {
      return 'daemon · running';
    }

    return 'daemon · idle';
  };

  const daemonLabelClass = () => {
    if (!daemonData) {
      return 'text-foreground-muted';
    }

    if (daemonData.stale) {
      return 'text-score-low';
    }

    if (daemonData.running) {
      return 'text-accent';
    }

    return 'text-foreground';
  };

  const lastScanLabel = () => {
    if (lastScanData === undefined) {
      return 'last scan …';
    }

    if (lastScanData === null) {
      return 'no scans yet';
    }

    return `last scan ${formatRelative(lastScanData.startedAt)}`;
  };

  return (
    <Toolbar.Root
      className="relative flex h-status-bar shrink-0 items-center gap-3 border-t border-border bg-background px-3 font-mono text-xs text-foreground-muted"
      render={<footer />}
    >
      <DaemonStatusPopover
        data={daemonData}
        isError={daemonError}
        onOpenChange={setDaemonOpen}
        onRunNow={() => runScan.mutate()}
        open={daemonOpen}
        runPending={runScan.isPending}
      >
        <div
          className={cx(
            'flex h-5 items-center gap-2 rounded-md border border-border bg-surface/60 px-2 transition-colors hover:border-border-active hover:text-foreground',
          )}
        >
          <span className="relative flex size-1.5">
            {daemonData?.running ? (
              <>
                <span className="absolute inline-flex size-1.5 animate-ping rounded-full bg-accent opacity-75" />
                <span className="relative inline-flex size-1.5 rounded-full bg-accent" />
              </>
            ) : daemonData?.stale ? (
              <span className="relative inline-flex size-1.5 rounded-full bg-score-low" />
            ) : (
              <span className="relative inline-flex size-1.5 rounded-full bg-score-high" />
            )}
          </span>
          <span className={daemonLabelClass()}>{daemonLabelText()}</span>
          <span className="text-subtle">·</span>
          <span>
            next {computeNextLabel(daemonData?.nextScanAt, daemonData?.running)}
          </span>
        </div>
      </DaemonStatusPopover>

      <LastScanPopover
        data={lastScanData}
        isError={lastScanError}
        onOpenChange={setLastScanOpen}
        open={lastScanOpen}
      >
        <div className="flex h-5 items-center gap-1.5 rounded-md px-1.5 hover:bg-border/40 hover:text-foreground">
          <span>{lastScanLabel()}</span>
        </div>
      </LastScanPopover>

      <div className="ml-auto flex items-center gap-2">
        <span className="text-subtle">density</span>
        <ToggleGroup<Density>
          className="flex rounded-sm border border-border bg-surface p-0.5"
          onValueChange={(values) => {
            const [next] = values;

            if (next) {
              setDensity(next);
            }
          }}
          value={[density]}
        >
          {densityOptions.map((d) => (
            <HoverTip key={d.id} placement="top" text={d.tooltip}>
              <Toggle<Density>
                aria-label={d.tooltip}
                className={cx(
                  'h-4 w-6 rounded-sm text-base leading-none transition-colors data-pressed:bg-border/60 data-pressed:text-foreground data-unpressed:text-subtle data-unpressed:hover:text-foreground',
                )}
                value={d.id}
              >
                {d.label}
              </Toggle>
            </HoverTip>
          ))}
        </ToggleGroup>
        <KeyboardShortcutKey>D</KeyboardShortcutKey>
      </div>
    </Toolbar.Root>
  );
};
